import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import {Modal} from "react-bootstrap";
import {ApiError, ConfigApi, OrderType} from "../../../../services/Api/types";
import StoreUtils from "../../../../services/Store/StoreUtils";
import IframeWidget from "../../../../utils/IframeWidget";
import ErrorFormAlertView from "../../../Common/ErrorFormAlertView";
import QRCodeScannerComponent from "../../../Common/QRCodeScannerComponent";
import {api} from "../../../../services/Api/api";
import LoadingContainer from "../../../Common/LoadingContainer";
import ScannerUtils from "../../../../services/Scanner/ScannerUtils";

interface Props {
    show: boolean;
    onHide: () => void;
    currentType: OrderType | "";
    handleSave: (room: string) => void;
    config: ConfigApi | undefined;
    type: OrderType.ROOM_SERVICE | OrderType.DINE_IN;
}

const CartChangeOrderDetailsScanModal = ({show, onHide, handleSave, config, type}: Props) => {
    const {t} = useTranslation();
    const [value, setValue] = useState("");
    const [errors, setErrors] = useState<ApiError[]>([]);
    const [loading, setLoading] = useState(false);

    const handleScanQrToken = async (url: string) => {
        const token = ScannerUtils.extractToken(url);
        if (!token) {
            setErrors([{message: t("constraints.internal.wrong_qrcode_link")}])
            return;
        }
        try {
            setLoading(true);
            const response = await api.getQRCode(token);
            if (response && response.domain == config?.domain && response.number && response.type === type) {
                handleSave(response.number);
                IframeWidget.onHide(onHide);
            } else {
                setErrors([{message: t("constraints.internal.wrong_qrcode_link")}])
            }
        } catch (e: any) {
            setErrors([{message: t("constraints.internal.wrong_qrcode_link")}])
        } finally {
            setLoading(false);
        }
    }

    const handleScan = async (data: string) => {
        if (!data) {
            setErrors([{message: t("constraints.internal.wrong_qrcode_link")}]);
            return;
        }

        if (data.startsWith("http://") || data.startsWith("https://")) {
            if (data.includes("qr.goorder") || data.includes("demoqr.")) {
                await handleScanQrToken(data);
                return;
            }

            try {
                const url = new URL(data);
                const pathSegments = url.pathname.split('/').filter(Boolean);
                const orderType = pathSegments[pathSegments.length - 1];
                if (!StoreUtils.isSameDomain(data, config?.domain) || !orderType ||
                    (orderType !== type)
                ) {
                    setErrors([{message: t("constraints.internal.wrong_qrcode_link")}]);
                    return;
                }
                const numberParam = url.searchParams.get("number");
                if (numberParam) {
                    handleSave(numberParam);
                    IframeWidget.onHide(onHide);
                } else {
                    setErrors([{message: t("constraints.internal.wrong_qrcode_link")}]);
                }
            } catch (error) {
                setErrors([{message: t("constraints.internal.wrong_qrcode_link")}]);
            }
        } else {
            setErrors([{message: t("constraints.internal.wrong_qrcode_link")}]);
        }
    }

    const handleSaveClick = () => {
        handleSave(value);
        IframeWidget.onHide(onHide)
    }

    return (
        <Modal dialogClassName="modal-full" show={show} onHide={() => IframeWidget.onHide(onHide)}
               className="order-details-scan-modal" onShow={() => IframeWidget.onShow()}>
            <Modal.Header closeButton>
                <Modal.Title>{type === OrderType.ROOM_SERVICE ?
                    t("modules.cart.field.room_number_choice.title") :
                    t("modules.cart.field.table_choice.title")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    !config?.ordering.scanner_disabled && (
                        <p className="text-muted">{type === OrderType.ROOM_SERVICE ?
                            t("modules.cart.field.scan_qr_room_code.title") :
                            t("modules.cart.action.scan_table_qr_code.title")}
                        </p>
                    )
                }
                {!config?.ordering.scanner_disabled ? (
                    <>
                        {errors.length > 0 ? (
                            <>
                                <ErrorFormAlertView errors={errors}/>
                                <button className={"btn btn-default btn-lg w-100 mb-4"}
                                        onClick={() => setErrors([])}>
                                    {t("common.action.scan_again")}
                                </button>
                            </>
                        ) : (
                            <>
                                {loading ? (
                                    <div>
                                        <LoadingContainer> {t("common.word.processing_in_progress")}</LoadingContainer>
                                    </div>
                                ) : (
                                    <QRCodeScannerComponent onScan={handleScan}/>
                                )}
                            </>
                        )}
                        {type === OrderType.DINE_IN && config?.ordering.number_editable && (
                            <>
                                <div className="text-with-line">
                                    <hr/>
                                    <small>{t("modules.cart.field.fill_manually.title")}</small>
                                    <hr/>
                                </div>
                                <div className="form-group mt-4">
                                    <input type="text" value={value}
                                           onChange={e => setValue(e.target.value)}
                                           className="form-control"
                                           id="table"
                                           placeholder={t("modules.table_number_modal.field.table_number.title")}/>
                                    <label htmlFor="table">
                                        {t("modules.table_number_modal.field.table_number.title")}
                                    </label>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div className="form-group mt-4">
                        <input type="text" value={value}
                               onChange={e => setValue(e.target.value)}
                               className="form-control"
                               id="table"
                               placeholder={t("modules.table_number_modal.field.table_number.title")}/>
                        <label htmlFor="table">
                            {t("modules.table_number_modal.field.table_number.title")}
                        </label>
                    </div>
                )}
            </Modal.Body>
            {
                type === OrderType.DINE_IN && config?.ordering.number_editable && (
                    <Modal.Footer>
                        <button className="btn btn-default" onClick={() => IframeWidget.onHide(onHide)}>
                            {t("common.action.cancel")}
                        </button>
                        <button className="btn btn-primary" onClick={handleSaveClick} disabled={!value}>
                            {t("common.action.save")}
                        </button>
                    </Modal.Footer>
                )
            }
        </Modal>
    )
}

export default CartChangeOrderDetailsScanModal;